/**
 * v-ai-selectable 指令
 * ============================================
 * 把一个 DOM 区域注册为「AI 助手可选取区域」。
 * 侧边栏进入选取模式后,这些区域会被高亮,点击即可把内容读入对话上下文。
 *
 * 用法(绑定值支持三种形式):
 *
 * 1. 字符串(仅作为名称,内容取 el.innerText):
 *    <div v-ai-selectable="'分镜脚本'">...</div>
 *
 * 2. 对象(推荐):
 *    <div v-ai-selectable="{
 *      type: 'video_prompt',                       // 选区类型(用于路由提示词里的类型说明)
 *      label: '镜号 ' + seg.number + ' 画面描述',   // 展示名称
 *      getText: () => seg.grokPrompt.parameters.prompt,  // 文本读取器(可选,缺省取 innerText)
 *      getImage: () => seg.thumbnail,              // 图片读取器(可选,图片区域用)
 *      apply: (t) => applyAiVideoPromptText(seg, t), // 写回器(可选;提供后 AI 修改可一键应用)
 *      disabled: false,                            // 临时禁用(可选)
 *    }">...</div>
 *
 * 3. 函数(每次解析时调用,返回上面的对象;适合内容随状态切换的场景)
 *
 * 注意:getText/getImage/apply 都是惰性调用,指令更新时会自动拿到最新闭包,
 * 因此放在 v-for 里也能正确对应到各自的数据项。
 *
 * ── 悬浮「编辑」小按钮 ──────────────────────────
 * 对「带图的选区」(配置里有 getImage 或 image),指令会在元素左上角注入一个
 * 默认隐藏、鼠标移上去才显示的「✎ 编辑」按钮。点击它会直接打开 AI 编辑侧边栏,
 * 并把该选区自动选入上下文(无需先进选取模式)。文本类选区不注入此按钮。
 */

import {
  registerAiRegion,
  updateAiRegion,
  unregisterAiRegion,
  openAiSidebar,
} from '@/utils/aiAssistant';

/* 全局只注入一次的样式(按钮外观 + 悬浮显示规则) */
const STYLE_ID = 'ai-selectable-edit-badge-style';
function ensureBadgeStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    '.ai-edit-badge{',
    '  position:absolute; top:6px; left:6px; z-index:50;',
    '  display:none; align-items:center; gap:3px;',
    '  height:24px; padding:0 9px; margin:0;',
    '  font-size:12px; line-height:1; white-space:nowrap; color:#fff;',
    '  background:rgba(124,77,255,.92); border:none; border-radius:13px;',
    '  cursor:pointer; box-shadow:0 2px 8px rgba(17,12,46,.28);',
    '  transition:background .15s ease; user-select:none;',
    '}',
    '.ai-edit-badge:hover{ background:#7c4dff; }',
    '[data-ai-selectable]:hover > .ai-edit-badge{ display:inline-flex; }',
  ].join('\n');
  (document.head || document.documentElement).appendChild(style);
}

/* 判断该选区是否带图(只有带图选区才加「编辑」按钮) */
function hasImage(value) {
  try {
    const v = typeof value === 'function' ? value() : value;
    if (!v || typeof v !== 'object') return false;
    if (v.disabled) return false;
    return typeof v.getImage === 'function' || (typeof v.image === 'string' && !!v.image);
  } catch (e) {
    return false;
  }
}

/* 给元素挂上「编辑」按钮(幂等;被框架 patch 掉后会自动重挂) */
function attachBadge(el) {
  if (!el) return;
  ensureBadgeStyle();
  // 已创建过:若被 Vue patch 移出 DOM,则重新挂回
  if (el._aiEditBadge) {
    if (!el.contains(el._aiEditBadge)) el.appendChild(el._aiEditBadge);
    return;
  }
  // 需要定位上下文;原本是 static 才改成 relative(并记录,卸载时还原)
  const pos = window.getComputedStyle(el).position;
  if (pos === 'static' || !pos) {
    el.style.position = 'relative';
    el._aiBadgeSetRelative = true;
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ai-edit-badge';
  btn.title = 'AI 编辑这张图';
  btn.innerHTML = '<span style="font-size:13px;line-height:1;">\u270E</span><span>编辑</span>';
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openAiSidebar({ selectEl: el, editImage: true }); // 确定要改这张图,打开后直接进入图像编辑
  };
  // 拦住 mousedown/click,避免触发卡片本身的点击(打开它自己的弹窗等)
  const swallow = (e) => { e.stopPropagation(); };
  btn.addEventListener('mousedown', swallow, true);
  btn.addEventListener('click', onClick, true);
  el._aiEditBadge = btn;
  el._aiEditBadgeClick = onClick;
  el._aiEditBadgeSwallow = swallow;
  el.appendChild(btn);
}

/* 移除「编辑」按钮并还原定位 */
function removeBadge(el) {
  if (!el) return;
  if (el._aiEditBadge) {
    try { el._aiEditBadge.removeEventListener('click', el._aiEditBadgeClick, true); } catch (e) { /* noop */ }
    try { el._aiEditBadge.removeEventListener('mousedown', el._aiEditBadgeSwallow, true); } catch (e) { /* noop */ }
    try { if (el.contains(el._aiEditBadge)) el.removeChild(el._aiEditBadge); } catch (e) { /* noop */ }
    el._aiEditBadge = null;
    el._aiEditBadgeClick = null;
    el._aiEditBadgeSwallow = null;
  }
  if (el._aiBadgeSetRelative) {
    el.style.position = '';
    el._aiBadgeSetRelative = false;
  }
}

/* 按当前配置同步按钮(有图则挂、无图则撤) */
function syncBadge(el, value) {
  if (hasImage(value)) attachBadge(el);
  else removeBadge(el);
}

export default {
  inserted(el, binding) {
    registerAiRegion(el, binding.value);
    syncBadge(el, binding.value);
  },
  componentUpdated(el, binding) {
    updateAiRegion(el, binding.value);
    syncBadge(el, binding.value);
  },
  update(el, binding) {
    updateAiRegion(el, binding.value);
    syncBadge(el, binding.value);
  },
  unbind(el) {
    removeBadge(el);
    unregisterAiRegion(el);
  },
};
